import { useEffect, useRef, useState } from 'react';
import { generateQrImage } from '../importer/qrGenerate';
import { decodeQrFromImage } from '../importer/qrDecode';

interface QRCanvasProps {
  payload: string;
  sizePx?: number;
  className?: string;
}

/**
 * Renders a QR code by drawing our own pure black/white pixel buffer
 * (see qrGenerate.ts) with putImageData — never stroked paths — so there
 * is no anti-aliasing to blur module edges. Before declaring itself
 * "ready" it self-decodes the freshly drawn image and compares against the
 * source payload (spec: "Re-render and self-decode the displayed QR before
 * considering it ready. If self-validation fails, show a blocking error
 * rather than a possibly wrong code.").
 */
export function QRCanvas({ payload, sizePx = 320, className }: QRCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'validating' | 'ready' | 'error'>('validating');

  useEffect(() => {
    setState('validating');
    const canvas = canvasRef.current;
    if (!canvas) return;

    const image = generateQrImage(payload, { modulePixels: 10, quietZoneModules: 4 });
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      setState('error');
      return;
    }
    const imgData = ctx.createImageData(image.width, image.height);
    imgData.data.set(image.data);
    ctx.putImageData(imgData, 0, 0);

    // Self-decode the exact pixels we just drew.
    const redrawn = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const decoded = decodeQrFromImage(redrawn);
    setState(decoded && decoded.data === payload ? 'ready' : 'error');
  }, [payload]);

  return (
    <div className={`qr-canvas-wrap ${className ?? ''}`} data-state={state}>
      <canvas
        ref={canvasRef}
        style={{ width: sizePx, height: sizePx }}
        role="img"
        aria-label={state === 'ready' ? 'QR code, verified' : 'QR code'}
      />
      {state === 'error' && (
        <div className="qr-canvas-error" role="alert">
          This QR code failed self-validation. Do not photograph this screen — reload the app or re-import this job.
        </div>
      )}
    </div>
  );
}
